import dayjs from 'dayjs';

// Shape is checked explicitly rather than via dayjs(value, format, true):
// this app never loads the customParseFormat plugin, so dayjs silently ignores
// the format and strict arguments.
const yyyymmddRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

const MAX_AGE_YEARS = 100;

// SA ID number layout: YYMMDDSSSSCAZ - the date of birth is the first 6 digits.
// Capturing the parts here also enforces the full 13-digit length.
const saIdDobRegex = /^(\d{2})(\d{2})(\d{2})\d{7}$/;

// Two-digit years above this belong to the 1900s. Mirrors the backend's rule in
// vl-personal-details/mapping.js.
const CENTURY_PIVOT_YEAR = 24;

/**
 * Date-picker bound helpers for FSL-19280.
 *
 * Every export is a function rather than a constant on purpose. A module-level
 * `new Date()` is captured when the bundle first loads, which goes stale in a
 * long-lived SPA session and silently across midnight. FormBuilder calls these
 * per render via resolveDateBounds.
 *
 * react-date-picker treats an Invalid Date as a real bound and disables the
 * entire calendar, so anything unparseable must come back as null.
 */

export const getToday = () => new Date();

export const isIsoDate = (value) => yyyymmddRegex.test(value ?? '');

/**
 * Oldest selectable date, matching the 100-year ceiling the Yup schemas enforce.
 *
 * The schemas use a strict `isAfter(hundredYearsAgo)`, so a date landing exactly
 * 100 years ago is selectable in the picker but rejected on submit. That
 * one-day edge is left to the post-capture message (AC5) rather than skewing
 * the picker bound away from the documented rule.
 */
export const getHundredYearFloor = () => dayjs().subtract(MAX_AGE_YEARS, 'year').toDate();

/**
 * Derives a date of birth from the first six digits of an SA ID number.
 *
 * Mirrors validateAndExtractSAID in the backend's vl-personal-details/mapping.js,
 * including its `yy > 24 -> 19xx` century rule. The two must agree, otherwise the
 * picker would allow a date the API then rejects.
 */
export const deriveDobFromSaId = (idNumber) => {
  const parts = saIdDobRegex.exec(idNumber ?? '');

  if (!parts) {
    return undefined;
  }

  const [, yy, mm, dd] = parts;
  const yearPrefix = parseInt(yy, 10) > CENTURY_PIVOT_YEAR ? '19' : '20';

  return `${yearPrefix}${yy}-${mm}-${dd}`;
};

/**
 * Resolves the customer's date of birth from the live form values (AC6).
 *
 * Precedence mirrors resolveDateOfBirth in the backend's vl-personal-details/
 * mapping.js: on the SA ID journey the DoB the API will store is derived from
 * the ID number, so the derived value has to win here too. A returning customer
 * arrives with dateOfBirth already prefilled from GET /personal-detail, and
 * preferring that would pin the picker's floor to the *old* ID number - the
 * customer could then correct their ID and still pick a date the API rejects.
 *
 * The prefilled value is still the fallback, so the floor stays meaningful while
 * the ID number is mid-edit and therefore not yet derivable.
 *
 * Passport journeys render dateOfBirth as a real field, and their idNumber slot
 * holds the passport number, so nothing is derived from it.
 */
export const resolveDateOfBirth = (values) => (values?.idType === 'id'
  ? deriveDobFromSaId(values?.idNumber) || values?.dateOfBirth
  : values?.dateOfBirth);

/**
 * Minimum selectable "when did you move in" date: the customer's date of birth.
 *
 * Falls back to the 100-year floor when DoB cannot be resolved, so the calendar
 * stays usable before an ID number has been captured instead of locking up on
 * an Invalid Date.
 */
export const getMoveInFloor = (values) => {
  const dateOfBirth = resolveDateOfBirth(values);

  if (!yyyymmddRegex.test(dateOfBirth ?? '')) {
    return getHundredYearFloor();
  }

  const parsed = dayjs(dateOfBirth);
  return parsed.isValid() ? parsed.toDate() : getHundredYearFloor();
};
